import type { ReactElement, ReactNode } from 'react'
import type { Tone } from '~/charts/tokens'

/**
 * The four the design system has, and the only four there will be.
 *
 * A tone picks the fill, the edge, the ink and the glyph — four treatments that
 * differ from each other without colour. It is not the words: `Delayed`,
 * `Stale` and `Expires in 12 days` are three labels over `caution`, and a fifth
 * thing a source can be needs a label rather than a fifth tone.
 */
export type ChipTone = 'live' | 'caution' | 'failed' | 'manual'

/**
 * A tick, a clock, a triangle and a pencil — the glyph the mockups draw, matched
 * to the tone rather than to the words, since the words are free text and the
 * tone is one of only four values.
 *
 * Decorative only: the label already carries the meaning (`aria-hidden`), and
 * the path draws in `currentColor` so it takes the ink `data-tone` already set
 * rather than adding a colour of its own.
 */
function ToneGlyph({ tone }: { tone: ChipTone }): ReactElement {
  switch (tone) {
    case 'live':
      return (
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M2.5 6.3 L5 8.8 L9.5 3.3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    case 'caution':
      return (
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <circle cx="6" cy="6" r="4.4" stroke="currentColor" strokeWidth="1.3" />
          <path d="M6 3.6 V6.2 L7.9 7.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    case 'failed':
      return (
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M6 2.2 L10.3 9.5 H1.7 Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
        </svg>
      )
    case 'manual':
      return (
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M2 9.7 L2.5 7.4 L7.5 2.4 L9.4 4.3 L4.4 9.3 Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" strokeLinecap="round" />
          <path d="M6.6 3.3 L8.5 5.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      )
  }
}

/**
 * The chip, as two props that are not the same question.
 *
 * `tone` is the visual token and there are four of them; `label` is situational
 * copy and there is no limit on it. They were one prop, which is how the
 * product came to read as though it had a vocabulary of its own competing with
 * the system's.
 */
export function StatusPill({
  tone,
  label,
  glyph = false,
}: {
  tone: ChipTone
  label: ReactNode
  /** Off by default: most callers are not reporting a source's connection
   * state, and the four glyphs only mean something there. */
  glyph?: boolean
}): ReactElement {
  return (
    <span className="nw-status" data-tone={tone}>
      {glyph && <ToneGlyph tone={tone} />}
      {label}
    </span>
  )
}

/** Two or three letters standing in for a face nobody uploaded. */
export function Avatar({ label, initials }: { label: string; initials: string }): ReactElement {
  return (
    <span className="nw-avatar" title={label} aria-label={label} role="img">
      {initials}
    </span>
  )
}

/**
 * The tile at the head of a row, tinted for what it stands for.
 *
 * A property, a fund, a coin, a pension, a bank balance and a debt are six
 * different kinds of thing, and the tiles are read straight down the left edge
 * of a long list — so the colour does work there that it would not do anywhere
 * else on the page.
 */
export function Monogram({
  initials,
  tone = 'neutral',
  size = 'md',
}: {
  initials: string
  tone?: Tone | 'neutral'
  size?: 'sm' | 'md'
}): ReactElement {
  return (
    <span className="nw-monogram" data-tone={tone} data-size={size} aria-hidden="true">
      {initials}
    </span>
  )
}

/**
 * Which of the six tints a holding's tile wears.
 *
 * A private stake, a fund, a coin and a bank balance are different kinds of
 * thing, and the tile is read straight down the left edge of a long list —
 * before the row itself is read. Nothing in the instrument kinds names a debt,
 * so what is owed is told apart by its sign instead.
 */
export function toneOfHolding(kind: string, valueMinor: number | null): Tone | 'neutral' {
  if (valueMinor !== null && valueMinor < 0) return 'liability'
  if (kind === 'cash') return 'cash'
  if (kind === 'crypto') return 'crypto'
  if (kind === 'private') return 'property'
  if (kind === 'equity' || kind === 'etf' || kind === 'fund') return 'equity'
  return 'neutral'
}

/**
 * A monogram, a name, a line of detail, and where it stands.
 *
 * The tile's colour is a `tint` here rather than a second `tone`: it says what
 * kind of thing the source is, which is a different question from how the
 * connection is doing, and one word for both was how they got confused.
 */
export function SourceRow({
  initials,
  name,
  detail,
  tone,
  label,
  tint,
}: {
  initials: string
  name: string
  detail: string
  tone: ChipTone
  label: string
  tint?: Tone | 'neutral'
}): ReactElement {
  return (
    <div className="nw-source">
      <Monogram initials={initials} tone={tint} />
      <span className="nw-source-name">
        <span>{name}</span>
        <small>{detail}</small>
      </span>
      <StatusPill tone={tone} label={label} glyph />
    </div>
  )
}

export interface Notice {
  id: string
  /** Neutral for what is worth knowing and wrong with nothing, as fewer sources is. */
  tone: 'warning' | 'critical' | 'neutral'
  text: ReactNode
}

/**
 * The things worth a look, each with a dot that says how much.
 *
 * A list rather than a stack of banners: four full-width alarm bars on one page
 * read as an outage, and none of these is one.
 */
export function NoticeList({ notices }: { notices: readonly Notice[] }): ReactElement | null {
  if (notices.length === 0) return null
  return (
    <ul className="nw-notices">
      {notices.map((notice) => (
        <li key={notice.id} data-tone={notice.tone}>
          <span className="nw-notice-dot" aria-hidden="true" />
          <span>{notice.text}</span>
        </li>
      ))}
    </ul>
  )
}
